/**
 * テーマカラー編集 (org-admin の theme override) で使う色操作ヘルパー。
 *
 * 入出力はすべて #RRGGBB の HEX 文字列を想定。
 */

/** "#RRGGBB" → [r, g, b] (各 0-255)。不正な入力時は null。 */
function parseHex(hex: string): [number, number, number] | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

/** [r, g, b] → "#RRGGBB" */
function formatHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  const to2 = (v: number) => clamp(v).toString(16).padStart(2, '0');
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

/** "#RRGGBB" → "R G B" 形式 (CSS `rgba(var(--x) / 0.2)` 用)。不正なら null。 */
export function hexToRgbTriplet(hex: string): string | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  return `${rgb[0]} ${rgb[1]} ${rgb[2]}`;
}

/**
 * HEX を amount (0-1) ぶん暗色化した HEX を返す。RGB 各チャネルに (1 - amount)
 * を乗算する単純なスカラー方式 (CSS の `filter: brightness(1 - amount)` 相当)。
 *
 * 例: darken('#2563FF', 0.15) → '#1F54D9' (同じ青味のまま 15% 暗い)
 *
 * NOTE: 以前は HSL.L を下げる実装だったが、彩度が高い色 (例 #7c5dff の purple)
 * では L を下げても青チャネル 255 が変わらず、結果が pure blue に見えるという
 * 知覚的に不自然な挙動になっていた。RGB スカラー方式の方が「全体的に少し暗く
 * なった同じ色」というユーザー期待に合う。不正な HEX は入力をそのまま返す。
 */
export function darken(hex: string, amount: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const factor = Math.max(0, 1 - amount);
  return formatHex(rgb[0] * factor, rgb[1] * factor, rgb[2] * factor);
}

/**
 * 2 つの HEX 間の WCAG コントラスト比 (1-21)。文字色 vs 背景色の可読性チェックに使う。
 * 4.5 以上で AA 通常テキスト、3.0 以上で AA 大テキスト相当。
 */
export function contrastRatio(hexA: string, hexB: string): number {
  const lum = (hex: string): number | null => {
    const rgb = parseHex(hex);
    if (!rgb) return null;
    const [r, g, b] = rgb.map(v => {
      const c = v / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  const la = lum(hexA);
  const lb = lum(hexB);
  if (la === null || lb === null) return 1;
  const [lighter, darker] = la > lb ? [la, lb] : [lb, la];
  return (lighter + 0.05) / (darker + 0.05);
}

/** HEX 形式の妥当性チェック (#RRGGBB のみ許容)。 */
export function isValidHex(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value);
}

/** HEX の相対輝度 (WCAG, 0-1)。不正なら null。 */
function relativeLuminance(hex: string): number | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * 生成ステータスバー (サブカラー背景) 上に重ねる前景色のセット。
 *
 * サブカラーは org-admin が任意の色に設定しうるため、文字色をダーク固定で
 * ハードコードすると暗いサブカラー (例 hypeapp の青) では文字が潰れて読めない。
 * 背景輝度で「柔らかい黒 / 柔らかい白」を自動で切り替え、純黒・純白は避けて
 * 馴染ませつつ可読性を担保する。
 *
 * - text:  ステータステキスト。輝度ベースで反転。明るい背景でも純黒にはしない。
 * - ring:  点滅ドットの輪郭。明るい背景でも白ドットを視認させるための定義リング。
 * - glow:  点滅ドットの発光。常に白系で「ライブ感 (明るさ)」を出す。
 *
 * ドット本体は常に白にして「明るい」印象を優先し、視認性は ring で担保する。
 */
export function subBarInk(hex: string): { text: string; ring: string; glow: string } {
  const lum = relativeLuminance(hex);
  // 解析不能時は中〜濃色サブカラーが大半という前提で暗背景扱い (白前景)
  const isLight = lum !== null && lum > 0.5;
  return isLight
    ? {
        text: 'rgba(22, 17, 7, 0.80)',
        ring: 'rgba(22, 17, 7, 0.09)',
        glow: 'rgba(255, 255, 255, 0.4)',
      }
    : {
        text: 'rgba(255, 255, 255, 0.94)',
        ring: 'rgba(255, 255, 255, 0.1)',
        glow: 'rgba(255, 255, 255, 0.7)',
      };
}

/**
 * アクセントカラー塗りの面に乗る前景色 (不透明)。明るさの判定は subBarInk と同じ。
 * lucide の線に半透明色を継ぐと交点が光るため (rules/icons.md)、alpha を持たせない。
 */
export function accentInk(hex: string): string {
  const lum = relativeLuminance(hex);
  return lum !== null && lum > 0.5 ? '#161107' : '#ffffff';
}
